import useIsEmea from "@dana/Common/Hooks/useIsEmea";
import ModalSearchSection from "@dana/Components/OpenOrderDetails/ModalSearchSection";
import OpenOrderPagination from "@dana/Components/OpenOrderDetails/OpenOrderPagination";
import OrderLinesReviewScreen from "@dana/Components/OpenOrderDetails/OrderLinesReviewScreen";
import OrderLinesSelectScreen from "@dana/Components/OpenOrderDetails/OrderLinesSelectScreen";
import { themeBackgrounds, themeColors } from "@dana/MobiusOverrides/styles/colors";
import { fontWeights, lineHeights } from "@dana/MobiusOverrides/styles/typography";
import ApplicationStateDana from "@dana/Store/DanaApplicationState";
import GetOpenOrdersDetailData from "@dana/Store/Data/OpenOrders/Handlers/GetOpenOrdersDetailHandler";
import PostCancelLinesData from "@dana/Store/Data/OpenOrders/Handlers/PostCancelLinesHandler";
import {
    BackorderResponse,
    BackorderResponseBackorderInfo,
    CancelLine,
    CancelOrderRequest,
    CancelValidationResponse,
} from "@dana/Types/ApiModels";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import {
    Button,
    DataTableBody,
    DataTableCell,
    DataTableHead,
    DataTableHeader,
    DataTableRow,
    LoadingOverlay,
    Modal,
    TextField,
    Typography,
} from "@insite/mobius/index";
import { StyledClickable, StyledTypography } from "@insite/mobius/Link";
import { ModalPresentationProps } from "@insite/mobius/Modal";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useMemo, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// --- Props Interface ---
interface OpenOrderDetailsTableProps {
    openOrders: BackorderResponse;
}

type OrderLine = {
    lineNo: string;
    partNo: string;
    customerPartNo: string;
    description: string;
    orderedQty: number;
    cancelItemQty: number;
    cancel: boolean;
    notCancellableReason?: string;
};

type Step = "select" | "review" | "success";

// --- Styles Interfaces and Objects ---
export interface OpenOrderDetailsTableStyles {
    mainContainer?: InjectableCss;
    wrapper?: InjectableCss;
    container?: InjectableCss;
    orderDateHeader?: DataTableHeaderProps;
    orderNumberHeader?: DataTableHeaderProps;
    openQtyHeader?: DataTableHeaderProps;
    partNoHeader?: DataTableHeaderProps;
    customerPOHeader?: DataTableHeaderProps;
    orderTotalHeader?: DataTableHeaderProps;
    estdShipDateHeader?: DataTableHeaderProps;
    cancleItemsHeader?: DataTableHeaderProps;
    dataTable?: DataTableProps;
    orderNumberCells?: DataTableCellProps;
    modal?: ModalPresentationProps;
    modalTitleWrapper?: InjectableCss;
    headerPoNo?: TypographyPresentationProps;
    headerPoNoTitle?: TypographyPresentationProps;
    successStepMessageWrap?: InjectableCss;
    successStepMessage?: TypographyPresentationProps;
    modalButtons?: InjectableCss;
}

export const OpenOrderDetailsTableStyles: OpenOrderDetailsTableStyles = {
    mainContainer: {
        css: css`
            overflow: auto;
        `,
    },
    wrapper: {
        css: css`
            width: 100%;
        `,
    },
    container: {
        css: css`
            overflow: auto;
        `,
    },
    cancleItemsHeader: {
        tight: true,
    },
    dataTable: {
        mergeCss: true,
        css: css`
            td {
                padding: 14px;
            }
        `,
    },
    orderNumberCells: {
        css: css`
            padding: 16px;

            ${StyledClickable} {
                &:hover {
                    text-decoration: none;
                }
            }

            ${StyledTypography} {
                font-weight: ${fontWeights.semiBold};
                text-decoration: none;
            }

            .process-no {
                display: block;
                margin-top: 4px;
            }
        `,
    },
    modal: {
        size: 1140,
        sizeVariant: "large",
        headlineTypographyProps: {
            variant: "h4",
            css: css`
                margin-bottom: 0;
            `,
        },
    },
    modalTitleWrapper: {
        css: css`
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            width: 100%;
            padding: 0 32px 24px 0;
            gap: 32px;

            &.review-step,
            &.success-step {
                justify-content: flex-end;
            }

            .order-no-wrapper {
                display: flex;
                align-items: flex-start;
                gap: 32px;
            }

            .order-no-box {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
            }

            @media only screen and (max-width: 768px) {
                flex-direction: column;
            }
        `,
    },
    headerPoNoTitle: {
        color: themeBackgrounds.darkGray,
    },
    headerPoNo: {
        weight: fontWeights.semiBold,
    },
    successStepMessageWrap: {
        css: css`
            background: ${themeBackgrounds.success};
            border: 1px solid ${themeColors.successGreen};
            padding: 16px;
            margin-bottom: 24px;
        `,
    },
    successStepMessage: {
        color: themeColors.successGreen,
    },
    modalButtons: {
        css: css`
            margin: 16px 0;
            display: flex;
            align-items: center;
            gap: 32px;
            justify-content: center;
        `,
    },
};

const mapStateToProps = (state: ApplicationStateDana) => ({
    username: state.context.session.userName,
    salesNum: state.context.session.language?.properties["salesOrganization"],
    pageSize: state.context.settings.settingsCollection.websiteSettings.defaultPageSize,
    openOrderDetail: state.openOrdersDetail,
    postCancelLinesDetail: state.postCancelLinesDetail,
    isEmea: useIsEmea(state),
});

const mapDispatchToProps = {
    GetOpenOrdersDetailData,
    PostCancelLinesData,
};

const styles = OpenOrderDetailsTableStyles;

type sortByField = {
    [key: string]: string[];
};

const sortByFields: sortByField = {
    webOrderNumber: ["webOrderNumber"],
    customerPO: ["customerPO"],
    customerVatNumber: ["customerVatNumber"],
    partNo: ["partNo"],
};

type Props = OpenOrderDetailsTableProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

// --- Component Definition ---
const OpenOrderDetailsTable: FC<Props> = ({
    openOrders,
    pageSize,
    GetOpenOrdersDetailData,
    openOrderDetail,
    PostCancelLinesData,
    postCancelLinesDetail,
    username,
    salesNum,
    isEmea,
}) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [resultsPerPage, setResultsPerPage] = useState(pageSize);
    const startIndex = (currentPage - 1) * resultsPerPage;

    type SortableField = "salesOrderNumber" | "poNumber" | "secondryPO" | "partNumber" | "dateOrdered" | "customerPartNumber";
    const [selectsortField, setSelectSortField] = useState<SortableField>("salesOrderNumber");
    const [sortDirection, setSortDirection] = useState("desc");

    const sortedBackorders = openOrders?.backorders?.slice().sort((a, b) => {
        const fieldA = a[selectsortField]?.toString().toLowerCase() || "";
        const fieldB = b[selectsortField]?.toString().toLowerCase() || "";
        const comparison = fieldA.localeCompare(fieldB);
        return sortDirection === "asc" ? comparison : -comparison;
    });

    const paginatedData =
        openOrders !== undefined && openOrders?.backorders !== undefined && openOrders?.backorders.length > 0
            ? sortedBackorders.slice(startIndex, startIndex + resultsPerPage)
            : [];

    useEffect(() => {
        setCurrentPage(1);
    }, [openOrders]);

    const headerClick = (newsortField: SortableField) => {
        if (selectsortField === newsortField) {
            // Toggle direction if same field is clicked again
            setSortDirection(prev => (prev === "asc" ? "desc" : "asc"));
        } else {
            // Set new field and default to ascending
            setSelectSortField(newsortField);
            setSortDirection("asc");
        }
    };

    // --- Modal State & Flow ---
    const [isOpen, setIsOpen] = useState(false);
    const [activeOrder, setActiveOrder] = useState<OrderLine[] | null>(null);
    const [selectOrder, setSelectOrder] = useState<BackorderResponseBackorderInfo | null>(null);
    const [step, setStep] = useState<Step>("select");
    const [stepMsg, setstepMsg] = useState("");
    const [search, setSearch] = useState("");
    const [selectedLines, setSelectedLines] = useState<Set<string>>(new Set());
    const [errorMsg, seterrorMsg] = useState("");

    const [pendingOrderKey, setPendingOrderKey] = useState<string | null>(null);
    const makeOrderKey = (o: { salesOrderNumber: string; poNumber?: string | null }) =>
        `${o.salesOrderNumber}|${o.poNumber ?? ""}`;

    const openModalForOrder = (order: BackorderResponseBackorderInfo) => {
        const key = makeOrderKey(order);

        setSelectOrder(order);
        setPendingOrderKey(key);

        setOrderLines([]);
        setActiveOrder(null);

        setIsOpen(true);
        setStep("select");
        setSearch("");
        setSelectedLines(new Set());
        seterrorMsg("");

        GetOpenOrdersDetailData({
            orderNumber: order.salesOrderNumber,
            poNumber: order.poNumber || undefined,
        });
    };

    const [orderLines, setOrderLines] = useState<OrderLine[]>([]);

    useEffect(() => {
        
        if (!selectOrder || !openOrderDetail?.data) {return;}

        
        const expectedKey = makeOrderKey(selectOrder);
        if (pendingOrderKey !== expectedKey) {
            
            return;
        }

        const data = openOrderDetail.data as CancelValidationResponse | CancelValidationResponse[];
        let lines: OrderLine[] = [];

        if (Array.isArray(data)) {
            lines = data.map((line: CancelValidationResponse) => ({
                lineNo: line.lineNumber,
                cancel: line.cancelFlag !== "N",
                orderedQty: line.orderQuantity,
                cancelItemQty: line.eligibleCancelQuantity,
                partNo: line.danaPartNumber,
                description: line.partDescription,
                customerPartNo: line.customerPartNumber,
            }));
        } else if (data) {
            lines = [
                {
                    lineNo: data.lineNumber,
                    cancel: data.cancelFlag !== "N",
                    orderedQty: data.orderQuantity,
                    cancelItemQty: data.eligibleCancelQuantity,
                    partNo: data.danaPartNumber,
                    description: data.partDescription,
                    customerPartNo: data.customerPartNumber,
                },
            ];
        }

        setOrderLines(lines);
        setActiveOrder(lines);
    }, [openOrderDetail?.data, selectOrder, pendingOrderKey]);

    const closeModal = () => {
        setIsOpen(false);
        setActiveOrder(null);
        setStep("select");
        setSearch("");
        setSelectedLines(new Set());
        setPendingOrderKey(null);
    };

    const toggleLine = (lineNo: string) => {
        setSelectedLines(prev => {
            const next = new Set(prev);
            if (next.has(lineNo)) {
                next.delete(lineNo);
            } else {
                next.add(lineNo);
            }
            return next;
        });
    };

    const filteredLines = useMemo(() => {
        if (!activeOrder) {
            return [];
        }
        const term = search.trim().toLowerCase();
        if (!term) {
            return activeOrder;
        }
        return activeOrder?.filter(l => {
            return (
                l.lineNo?.toLowerCase().includes(term) ||
                l.partNo?.toLowerCase().includes(term) ||
                l.customerPartNo?.toLowerCase().includes(term) ||
                l.description?.toLowerCase().includes(term)
            );
        });
    }, [activeOrder, search]);

    const selectedObjects = useMemo(() => {
        if (!activeOrder) {
            return [];
        }
        const map = new Map(activeOrder.map(l => [l.lineNo, l]));
        return [...selectedLines].map(ln => map.get(ln)!).filter(Boolean) as OrderLine[];
    }, [activeOrder, selectedLines]);

    const atLeastOneSelected = selectedLines.size > 0;

    const HeaderOrderPoNo = (
        <StyledWrapper className="order-no-wrapper">
            <StyledWrapper className="order-no-box">
                <Typography {...styles.headerPoNoTitle}>{translate("Primary PO Number")}</Typography>
                <Typography {...styles.headerPoNo}>{selectOrder?.poNumber}</Typography>
            </StyledWrapper>
            <StyledWrapper className="order-no-box">
                <Typography {...styles.headerPoNoTitle}>{translate("Order Number")}</Typography>
                <Typography {...styles.headerPoNo}>{selectOrder?.salesOrderNumber}</Typography>
            </StyledWrapper>
        </StyledWrapper>
    );

    const modalTitle =
        step === "select"
            ? translate("Select Part Numbers to cancel")
            : step === "review"
            ? translate("Cancel Items")
            : translate("Cancelled Items");

    const handleBack = () => {
        if (step === "select") {
            return closeModal();
        }
        if (step === "review") {
            return setStep("select");
        }
        if (step === "success") {
            return closeModal();
        }
    };

    const handlePrimary = () => {
        if (step === "select" && atLeastOneSelected) {
            setStep("review");
            return;
        }
        if (step === "review") {
            // TODO: Call API to cancel selected lines if needed.

            const selectedLineNos = Array.from(selectedLines); // Convert Set<string> to array

            const orderMap = new Map();

            if (activeOrder) {
                for (const order of activeOrder) {
                    orderMap.set(order.lineNo, order);
                }
            }
            const filteredLines: OrderLine[] = [];
            for (const lineNo of selectedLineNos) {
                const matchedOrder = orderMap.get(lineNo);
                if (matchedOrder) {
                    filteredLines.push(matchedOrder);
                }
            }
            let cancelLines: CancelLine[] = [];

            if (filteredLines) {
                cancelLines = filteredLines.map((line: OrderLine) => ({
                    lineNumber: line.lineNo ?? "",
                    eligibleCancelQuantity: line.cancelItemQty,
                    partNumber: line.partNo ?? "",
                    orderNumber: selectOrder?.salesOrderNumber ?? "",
                    customerPoNumber: selectOrder?.poNumber ?? undefined,
                    userEmailAddress: username ?? "",
                    salesOrganization: salesNum ?? "",
                }));
            }

            const cancelOrderRequest: CancelOrderRequest = {
                lines: cancelLines,
            };
            PostCancelLinesData({
                cancelOrderRequest,
                onSuccess: () => {
                    // Handle success logic here
                },
                onComplete: resultProps => {
                    const apiResult = resultProps.apiResult;
                    if (apiResult.success) {
                        setStep("success");
                    } else {
                        seterrorMsg(apiResult.message);
                        setstepMsg("error");
                        setStep("review");
                    }
                },
                onError: (errorResponse: any) => {
                    seterrorMsg(translate("Failed due to some internal issue."));
                    setstepMsg("error");
                    setStep("review");
                },
            });
        }
    };

    const formatDate = (dateStr: string | null): string => {
        if (!dateStr) {
            return "-";
        } // fallback for null or undefined

        const date = new Date(dateStr);
        return  isEmea?date.toLocaleDateString("fr-FR"):date.toLocaleDateString();
    };

    const formatDateString = (dateStr: Date | null): string => {
       
        if (!dateStr) {
            return "-";
        } // fallback for null or undefined

        const date = new Date(dateStr);
        return  isEmea?date.toLocaleDateString("fr-FR"):date.toLocaleDateString();
    };

    const sorted = (sortField: string) => {
        let sorted: boolean | string = false;

        if (selectsortField === sortField) {
            if (sortDirection === "asc") {
                sorted = "ascending";
            } else {
                sorted = "descending";
            }
        } else if (selectsortField === `${sortField} DESC`) {
            sorted = "descending";
        }
        return sorted as SortOrderOptions;
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            {paginatedData?.length > 0 && (
                <>
                    <StyledWrapper {...styles.mainContainer}>
                        <DataTable {...styles.dataTable}>
                            <DataTableHead>
                                <DataTableHeader
                                    sorted={sorted("salesOrderNumber")}
                                    onSortClick={() => headerClick("salesOrderNumber")}
                                    {...styles.orderNumberHeader}
                                    title={translate("Order No.")}
                                >
                                    {translate("Order No.", "webOrderNumber")}
                                </DataTableHeader>
                                <DataTableHeader 
                                 sorted={sorted("dateOrdered")}
                                 onSortClick={() => headerClick("dateOrdered")}
                                 {...styles.orderDateHeader}>
                                    {translate("Order Date", "orderDate")}
                                </DataTableHeader>
                                <DataTableHeader
                                    sorted={sorted("poNumber")}
                                    onSortClick={() => headerClick("poNumber")}
                                    {...styles.customerPOHeader}
                                >
                                    {translate("Primary PO No.", "customerPO")}
                                </DataTableHeader>
                                <DataTableHeader
                                    sorted={sorted("secondryPO")}
                                    onSortClick={() => headerClick("secondryPO")}
                                    {...styles.customerPOHeader}
                                >
                                    {translate("Secondaty PO No.", "customerVatNumber")}
                                </DataTableHeader>
                                <DataTableHeader
                                    sorted={sorted("partNumber")}
                                    onSortClick={() => headerClick("partNumber")}
                                    {...styles.partNoHeader}
                                >
                                    {translate("Part No.", "partNo")}
                                </DataTableHeader>
                                {/* <DataTableHeader
                                    sorted={sorted("customerPartNumber")}
                                    onSortClick={() => headerClick("customerPartNumber")}
                                    {...styles.partNoHeader}
                                >
                                    {translate("Customer Part Number", "Customer Part Number")}
                                </DataTableHeader> */}
                                <DataTableHeader {...styles.openQtyHeader}>
                                    {translate("Open Qty.", "openQty")}
                                </DataTableHeader>
                                <DataTableHeader {...styles.estdShipDateHeader}>
                                    {translate("Estd. Ship Date", "estdShipDate")}
                                </DataTableHeader>
                                <DataTableHeader
                                    {...styles.cancleItemsHeader}
                                    title={translate("Cancel Items")}
                                ></DataTableHeader>
                            </DataTableHead>
                            <DataTableBody>
                                {paginatedData?.length > 0 &&
                                    paginatedData?.map((order, index) => (
                                        <DataTableRow key={index}>
                                            <DataTableCell {...styles.orderNumberCells}>
                                                <OrderDetailPageTypeLink
                                                    title={order.salesOrderNumber}
                                                    orderNumber={order.salesOrderNumber}
                                                    isVmiOrder={false}
                                                />
                                            </DataTableCell>
                                            <DataTableCell>{formatDateString(order?.dateOrdered)}</DataTableCell>
                                            <DataTableCell>{order.poNumber ?? translate("N/A")}</DataTableCell>
                                            <DataTableCell>{order.secondryPO ?? translate("N/A")}</DataTableCell>
                                            <DataTableCell>{order.partNumber ?? translate("N/A")}</DataTableCell>
                                            {/* <DataTableCell>{order.customerPartNumber ?? translate("N/A")}</DataTableCell> */}
                                            <DataTableCell>{order.quantityBackOrdered ?? translate("N/A")}</DataTableCell>
                                            <DataTableCell>{formatDate(order?.promisedDate)}</DataTableCell>
                                            <DataTableCell>
                                                <Button onClick={() => openModalForOrder(order)}>
                                                    {translate("Cancel Items")}
                                                </Button>
                                            </DataTableCell>
                                        </DataTableRow>
                                    ))}
                            </DataTableBody>
                        </DataTable>
                    </StyledWrapper>
                    <OpenOrderPagination
                        totalPages={Math.ceil(openOrders?.backorders?.length / resultsPerPage)}
                        onResultsPerPageChange={setResultsPerPage}
                        onPageChange={setCurrentPage}
                        currentPage={currentPage}
                    />
                </>
            )}
            {paginatedData?.length === 0 && (
                <>
                    <StyledWrapper>
                        <p>{translate("No open orders found.")}</p>
                    </StyledWrapper>
                </>
            )}
            <Modal
                headline={modalTitle}
                isOpen={isOpen}
                handleClose={closeModal}
                data-test-selector="orderCancellationModal"
                {...styles.modal}
            >
                {!openOrderDetail?.data ? (
                    <LoadingOverlay loading={!openOrderDetail?.data}></LoadingOverlay>
                ) : (
                    <StyledWrapper>
                        {/* Separate Search Section */}
                        {step === "select" && activeOrder && (
                            <>
                                <StyledWrapper {...styles.modalTitleWrapper}>
                                    <ModalSearchSection value={search} onChange={setSearch} />
                                    {HeaderOrderPoNo}
                                </StyledWrapper>
                                <OrderLinesSelectScreen
                                    lines={filteredLines}
                                    selected={selectedLines}
                                    onToggle={toggleLine}
                                />
                            </>
                        )}

                        {step === "review" && (
                            <>
                                <StyledWrapper {...styles.modalTitleWrapper} className="review-step">
                                    {HeaderOrderPoNo}
                                </StyledWrapper>
                                <OrderLinesReviewScreen lines={selectedObjects} />
                                {stepMsg === "error" && (
                                    <StyledWrapper {...styles.successStepMessageWrap}>
                                        <Typography role="status" aria-live="polite" {...styles.successStepMessage}>
                                            {errorMsg}
                                        </Typography>
                                    </StyledWrapper>
                                )}
                            </>
                        )}

                        {step === "success" && (
                            <>
                                <StyledWrapper {...styles.modalTitleWrapper} className="success-step">
                                    {HeaderOrderPoNo}
                                </StyledWrapper>
                                <StyledWrapper {...styles.successStepMessageWrap}>
                                    <Typography role="status" aria-live="polite" {...styles.successStepMessage}>
                                        {translate("Seleted Items cancelled successfully.")}
                                    </Typography>
                                </StyledWrapper>
                            </>
                        )}

                        <StyledWrapper {...styles.modalButtons}>
                            {step !== "success" && (
                                <Button variant="secondary" onClick={handleBack}>
                                    {translate("Back")}
                                </Button>
                            )}

                            {step === "select" && (
                                <Button variant="primary" onClick={handlePrimary} disabled={!atLeastOneSelected}>
                                    {translate("Cancel Items")}
                                </Button>
                            )}
                            {step === "review" && (
                                <Button variant="primary" onClick={handlePrimary}>
                                    {translate("Confirm")}
                                </Button>
                            )}
                            {step === "success" && (
                                <Button variant="primary" onClick={closeModal}>
                                    {translate("Done")}
                                </Button>
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                )}
            </Modal>
        </StyledWrapper>
    );
};
export default connect(mapStateToProps, mapDispatchToProps)(OpenOrderDetailsTable);
